import { borgAnalytics, getChannel, useTheme } from '@ytl/common-web';
import { AxiosError } from 'axios';
import { useCallback } from 'react';
import { useLanguage } from '../../contexts/languageContext';
import { useOpenAm } from '../../contexts/openAmContext';
import {
  borgMigrationAuthId,
  isResponseSuccess,
  OpenAmAuthenticateError,
  OpenAmAuthenticateResponse,
  OpenAmAuthenticateResponseOfBorgMigra,
  OpenAmAuthenticateResponseOfOAuth2Redirect,
  OpenAmAuthError,
  StageFailure,
} from '../@types/OpenAmInitialData';
import { LoginView, StageFailureType } from '../@types/OpenAmStage';
import { authenticationResource } from '../resource/authenticationResource';
import { LoginUtils } from '../utils/loginUtils';

export const useOpenAmStage = () => {
  const {
    stage,
    setStage,
    previousStage,
    authIdRenewEnabled,
    setAuthIdRenewEnabled,
    setIsLoading,
    authParams,
    setError,
    loadInitialData,
  } = useOpenAm();

  const { isWebView, isDark } = useTheme();
  const { language } = useLanguage();

  const isTypeRedirect = (value: any) => {
    return value?.type === 'RedirectCallback';
  };

  const isRedirectResponse = (response: any): response is OpenAmAuthenticateResponseOfOAuth2Redirect => {
    if (response?.stage === 'OAuth2' && response.callbacks && Array.isArray(response.callbacks)) {
      const cs = response.callbacks as any[];
      return cs.some(isTypeRedirect);
    } else {
      return false;
    }
  };

  const isBorgMigraResponse = (response: any): response is OpenAmAuthenticateResponseOfBorgMigra => {
    if (response?.stage === 'YettelBorgMigration3' && response.callbacks && Array.isArray(response.callbacks)) {
      const cs = response.callbacks as any[];
      return cs.some(value => value?.type === 'HiddenValueCallback');
    } else {
      return false;
    }
  };

  type FailureHandler = (res: StageFailure, mfa: boolean) => void;
  const handleOpenAmResponse = useCallback(
    (result: OpenAmAuthenticateResponse, method: string, failureHandler: FailureHandler) => {
      if (isResponseSuccess(result)) {
        if (method === 'sms') {
          borgAnalytics.sendLoginEvent({
            channel: getChannel(isWebView),
            virtualName: 'SMS Verification',
            method: 'sms',
            action: 'Sms verification',
            result: 'true',
            reason: 'undefined',
            externalId: result.tokenId,
            accountId: result.accountId,
          });
          borgAnalytics.sendLoginEvent({
            channel: getChannel(isWebView),
            virtualName: 'SMS Verification',
            method: 'sms',
            action: 'Second verification',
            result: 'true',
            reason: 'undefined',
            externalId: result.tokenId,
            accountId: result.accountId,
          });
        } else if (method === 'msisdn') {
          // MSISDN verificator is always the final step. Derive the original login method
          // from previousStage: if the step before MSISDN was SMSCode the user is an
          // SMS-login user; otherwise it was an email-login user.
          const isMsisdnAfterSms = previousStage?.view === LoginView.SMSCode;
          borgAnalytics.sendLoginEvent({
            channel: getChannel(isWebView),
            virtualName: isMsisdnAfterSms ? 'SMS Verification' : 'Login',
            method: isMsisdnAfterSms ? 'sms' : 'email',
            action: 'Second verification',
            result: 'true',
            reason: 'undefined',
            externalId: result.tokenId,
            accountId: result.accountId,
          });
        } else {
          borgAnalytics.sendLoginEvent({
            channel: getChannel(isWebView),
            virtualName: 'Login',
            method: method as 'email' | 'sms' | 'msisdn' | 'devicecode',
            action: 'First verification',
            result: 'true',
            reason: 'undefined',
            externalId: result.tokenId,
            accountId: result.accountId,
          });
        }

        setStage(LoginView.Loader, [], 'success');

        LoginUtils.navigateSuccess(authParams?.goto, result.successUrl, result.realm, () =>
          setError(OpenAmAuthError.OauthError),
        );
      } else if (isRedirectResponse(result)) {
        const redirectCallback = result.callbacks.find(isTypeRedirect);
        if (redirectCallback) {
          const target = redirectCallback.output.find(o => o.name === 'redirectUrl')?.value;
          if (target && typeof target === 'string') {
            window.location.href = target;
          }
        }
      } else if (isBorgMigraResponse(result)) {
        const r: OpenAmAuthenticateResponseOfBorgMigra = result;
        const targetUrlPrefix = r.callbacks[0]?.output[0]?.value;

        const returnUrl = new URL(window.location.href);
        returnUrl.searchParams.set(borgMigrationAuthId, r.authId);
        const encodedReturnUrl = encodeURI(returnUrl.toString());
        const targetUrl = new URL(targetUrlPrefix);
        targetUrl.searchParams.set('returnUrl', encodedReturnUrl);

        const linkParams = [isDark ? `theme=dark` : '', isWebView ? `app=true` : '', `locale=${language}`]
          .filter(s => !!s)
          .join('&');

        window.location.href = targetUrl.toString() + '&' + linkParams;
      } else if (result.stage === 'deviceLoginSuccess') {
        setStage(LoginView.DeviceCodeSuccess, [], 'deviceLoginSuccess');
        return;
      } else if (result.stage === 'deviceLoginFailed') {
        setStage(LoginView.DeviceCode, [], 'deviceLoginFailed');
        return;
      } else {
        const failure = parseFailure(result.header);
        const nextView = LoginUtils.getLoginViewByStage(result.stage);

        // Email+password accepted, transitioning to SMS or MSISDN stage → 'First verification'
        if (
          method === 'email' &&
          !failure &&
          (nextView === LoginView.SMSPhoneNumber ||
            nextView === LoginView.SMSCode ||
            nextView === LoginView.SMSCodeChange2FA ||
            nextView === LoginView.SMSNumberSelector ||
            nextView === LoginView.MSISDNCode)
        ) {
          borgAnalytics.sendLoginEvent({
            channel: getChannel(isWebView),
            virtualName: 'Login',
            method: 'email',
            action: 'First verification',
            result: 'true',
            reason: 'undefined',
            accountId: result.accountId,
          });
        }

        // SMS code accepted but flow continues to MSISDN verificator → fire 'Sms verification'
        // (the sms success branch only fires when SMS is the final step; here MSISDN is after SMS)
        if (method === 'sms' && !failure && nextView === LoginView.MSISDNCode) {
          borgAnalytics.sendLoginEvent({
            channel: getChannel(isWebView),
            virtualName: 'SMS Verification',
            method: 'sms',
            action: 'Sms verification',
            result: 'true',
            reason: 'undefined',
            accountId: result.accountId,
          });
        }

        setStage(nextView, result.infoText ?? [], result.stage, failure);
        if (failure) {
          failureHandler(failure, result.mfa ? result.mfa : false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setStage, authParams, setError, isWebView, isDark, language, previousStage],
  );

  const parseFailure = (f: string | undefined) => {
    try {
      if (f) {
        const res = JSON.parse(f);
        if (res.stagefailure && typeof res.stagefailure === 'string') {
          return res as StageFailure;
        }
      }
    } catch (e) {
      return undefined;
    }
  };

  const handleOpenAmErrorGeneric = useCallback(
    (err: unknown) => {
      if ((err as AxiosError).isAxiosError) {
        const axiosError = err as OpenAmAuthenticateError;
        if (axiosError.response?.data?.message === 'error-subscriptioncheck-subscription') {
          // setError(OpenAmAuthError.SubscriptionCheckError);
        } else if (axiosError.response?.data?.message === 'smstrustedlogin-callback-error') {
          // setError(OpenAmAuthError.SmsTrustedLoginCallbackError);
        } else if (axiosError.response?.status === 408) {
          setError(OpenAmAuthError.TimeoutError);
          return;
        } else if (axiosError.response?.data?.message.includes(StageFailureType.Blocked)) {
          setError(OpenAmAuthError.Blocked);
          return;
        } else {
          setError(OpenAmAuthError.LoginError);
        }
      } else {
        setError(OpenAmAuthError.LoginError);
      }
    },
    [setError],
  );

  const withTimeoutCheck = useCallback(
    async (func: () => Promise<OpenAmAuthenticateResponse>) => {
      try {
        return await func();
      } catch (err) {
        if ((err as AxiosError).isAxiosError) {
          const axiosError = err as OpenAmAuthenticateError;
          if (axiosError.response?.status === 408 && authIdRenewEnabled) {
            setAuthIdRenewEnabled(false);
            await loadInitialData();
            return await func();
          }
        }
        throw err;
      }
    },
    [setAuthIdRenewEnabled, authIdRenewEnabled, loadInitialData],
  );

  const loginFromStageWithUsernamePassword = useCallback(
    async (username: string, password: string, captchaValue: string, onFailure: FailureHandler) => {
      try {
        setIsLoading(true);
        const result = await withTimeoutCheck(() =>
          authenticationResource.postLogin(stage.authId!, username, password, captchaValue),
        );
        setIsLoading(false);
        handleOpenAmResponse(result, 'email', onFailure);
        return result;
      } catch (err) {
        handleOpenAmErrorGeneric(err);
        setIsLoading(false);
        throw err;
      }
    },
    [setIsLoading, handleOpenAmResponse, handleOpenAmErrorGeneric, withTimeoutCheck, stage.authId],
  );

  const loginWithPhoneNumber = useCallback(
    async (phoneNumber: string, onFailure: FailureHandler) => {
      try {
        setIsLoading(true);

        const result = await withTimeoutCheck(() =>
          authenticationResource.postPhoneNumber(stage.authId!, phoneNumber, stage.name!),
        );

        setIsLoading(false);

        handleOpenAmResponse(result, 'sms', onFailure);
      } catch (err) {
        handleOpenAmErrorGeneric(err);
        setIsLoading(false);
        throw err;
      }
    },
    [handleOpenAmResponse, setIsLoading, stage, handleOpenAmErrorGeneric, withTimeoutCheck],
  );

  const loginWithMSISDNVerificatorCode = useCallback(
    async (code: string, onFailure: FailureHandler) => {
      try {
        setIsLoading(true);
        const result = await withTimeoutCheck(() =>
          authenticationResource.postMSISDNVerificatorCode(stage.authId!, code),
        );
        setIsLoading(false);
        handleOpenAmResponse(result, 'msisdn', onFailure);
      } catch (err) {
        handleOpenAmErrorGeneric(err);
        setIsLoading(false);
        throw err;
      }
    },
    [handleOpenAmResponse, setIsLoading, stage, handleOpenAmErrorGeneric, withTimeoutCheck],
  );

  const reSendMSISDNVerificatorCode = useCallback(
    async (_code: string, onFailure: FailureHandler) => {
      try {
        setIsLoading(true);
        const result = await withTimeoutCheck(() => authenticationResource.reSendMSISDNVerificatorCode(stage.authId!));
        setIsLoading(false);
        handleOpenAmResponse(result, 'msisdn', onFailure);
      } catch (err) {
        handleOpenAmErrorGeneric(err);
        setIsLoading(false);
        throw err;
      }
    },
    [handleOpenAmResponse, setIsLoading, stage, handleOpenAmErrorGeneric, withTimeoutCheck],
  );

  const loginWithSMSCode = useCallback(
    async (smsCode: string, onFailure: FailureHandler) => {
      try {
        setIsLoading(true);
        let result;
        if (stage.view === LoginView.SMSCodeChange2FA) {
          result = await withTimeoutCheck(() => authenticationResource.postSMSCodeChange2FA(stage.authId!, smsCode));
        } else {
          result = await withTimeoutCheck(() => authenticationResource.postSMSCode(stage.authId!, smsCode));
        }
        setIsLoading(false);
        handleOpenAmResponse(result, 'sms', onFailure);
      } catch (err) {
        handleOpenAmErrorGeneric(err);
        setIsLoading(false);
        throw err;
      }
    },
    [handleOpenAmResponse, setIsLoading, stage, handleOpenAmErrorGeneric, withTimeoutCheck],
  );

  const loginWithAuthenticatorCode = useCallback(
    async (authenticatorCode: string, onFailure: FailureHandler) => {
      try {
        setIsLoading(true);
        const result = await withTimeoutCheck(() =>
          authenticationResource.postAuthenticatorCode(stage.authId!, authenticatorCode),
        );
        setIsLoading(false);
        handleOpenAmResponse(result, 'email', onFailure);
      } catch (err) {
        handleOpenAmErrorGeneric(err);
        setIsLoading(false);
        throw err;
      }
    },
    [handleOpenAmResponse, setIsLoading, stage, handleOpenAmErrorGeneric, withTimeoutCheck],
  );

  const loginWithOtpCode = useCallback(
    async (otpCode: string, onFailure: FailureHandler) => {
      try {
        setIsLoading(true);
        const result = await withTimeoutCheck(() => authenticationResource.postYoloOtpCode(stage.authId!, otpCode));
        setIsLoading(false);
        handleOpenAmResponse(result, 'email', onFailure);
      } catch (err) {
        handleOpenAmErrorGeneric(err);
        setIsLoading(false);
        throw err;
      }
    },
    [handleOpenAmResponse, setIsLoading, stage, handleOpenAmErrorGeneric, withTimeoutCheck],
  );

  const reSendOtp = useCallback(
    async (onFailure: FailureHandler) => {
      try {
        setIsLoading(true);
        const result = await withTimeoutCheck(() => authenticationResource.reSendYoloOtpCode(stage.authId!));
        setIsLoading(false);
        handleOpenAmResponse(result, 'email', onFailure);
      } catch (err) {
        handleOpenAmErrorGeneric(err);
        setIsLoading(false);
        throw err;
      }
    },
    [handleOpenAmResponse, setIsLoading, stage, handleOpenAmErrorGeneric, withTimeoutCheck],
  );

  const loginWithNumberSelector = useCallback(
    async (smsCode: string, onFailure: FailureHandler) => {
      try {
        setIsLoading(true);
        const result = await withTimeoutCheck(() => authenticationResource.postSMSCode(stage.authId!, smsCode));
        setIsLoading(false);
        handleOpenAmResponse(result, 'sms', onFailure);
      } catch (err) {
        handleOpenAmErrorGeneric(err);
        setIsLoading(false);
        throw err;
      }
    },
    [handleOpenAmResponse, setIsLoading, stage, handleOpenAmErrorGeneric, withTimeoutCheck],
  );

  const continueTestLogin = useCallback(
    async (onFailure: FailureHandler) => {
      try {
        setIsLoading(true);
        const result = await withTimeoutCheck(() => authenticationResource.postTestLogin(stage.authId!));
        setIsLoading(false);
        handleOpenAmResponse(result, 'email', onFailure);
      } catch (err) {
        handleOpenAmErrorGeneric(err);
        setIsLoading(false);
        throw err;
      }
    },
    [handleOpenAmResponse, setIsLoading, stage, handleOpenAmErrorGeneric, withTimeoutCheck],
  );

  return {
    loginFromStageWithUsernamePassword,
    loginWithPhoneNumber,
    loginWithSMSCode,
    loginWithAuthenticatorCode,
    loginWithOtpCode,
    previousStage,
    reSendOtp,
    loginWithNumberSelector,
    continueTestLogin,
    loginWithMSISDNVerificatorCode,
    reSendMSISDNVerificatorCode,
  };
};
